import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/Button/Button';
import { Alert } from '@/components/Alert/Alert';
import { SchemaForm } from '@/components/SchemaForm/SchemaForm';
import type { JsonSchema } from '@/components/SchemaForm/types';
import { Tabs } from '@/components/Tabs/Tabs';
import { useJobTemplateDetail } from '@/features/library';
import { useUploadAttachment } from '@/features/attachments';
import { t } from '@/lib/i18n';
import type { PendingAttachment } from '@/features/members/components/ChatAttachmentPicker';
import { JobAttachmentTable } from '../components/JobAttachmentTable';
import {
  RecommendedMembersPanel,
  type AssigneeSelection,
} from '../components/RecommendedMembersPanel';
import { PendingAddBanner } from '../components/PendingHireBanner';
import { useCreateJob } from '../hooks';
import type { CreateJobBody } from '../schemas';
import styles from './JobNewPage.module.css';

/**
 * Shape that the RunJobSheet passes via location.state when the user clicks
 * "More options →" — preserves in-flight form work across the navigation.
 */
interface SheetHandoffState {
  templateId?: string;
  assignee?: AssigneeSelection;
  freeText?: string;
  prefillInputs?: Record<string, unknown> | null;
}

const FORM_ID = 'job-new-form';

function hasSchemaFields(schema: JsonSchema | null | undefined): boolean {
  return Boolean(schema?.properties && Object.keys(schema.properties).length > 0);
}

function initials(title: string): string {
  return title
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .filter(Boolean)
    .join('')
    .toUpperCase();
}

export function JobNewPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Template id comes from the URL query param. Anything missing → redirect
  // to the Jobs hub, which is now the discovery surface.
  const templateId = searchParams.get('template');
  const initialMemberId = searchParams.get('member');

  // The Sheet → "More options →" handoff stuffs in-flight form state into
  // location.state so the user doesn't lose work moving here.
  const handoff = (location.state ?? null) as SheetHandoffState | null;

  const [assignee, setAssignee] = useState<AssigneeSelection>(
    handoff?.assignee ?? (initialMemberId ? { kind: 'member', id: initialMemberId } : null),
  );
  const [title, setTitle] = useState('');
  const [instructions, setInstructions] = useState('');
  const [outputsDescription, setOutputsDescription] = useState('');
  const [freeText, setFreeText] = useState(handoff?.freeText ?? '');
  const [rememberOutputs, setRememberOutputs] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { data: templateResponse } = useJobTemplateDetail(templateId ?? undefined);
  const template = templateResponse?.item;
  const create = useCreateJob();
  const upload = useUploadAttachment();
  const hasPendingUploads = pendingAttachments.some((p) => !p.attachment && !p.error);
  const attachmentIds = pendingAttachments.filter((p) => p.attachment).map((p) => p.attachment!.id);

  // When a template loads (or changes), seed the editable title +
  // instructions + outputs description fields so the user can review or
  // edit before submitting.
  useEffect(() => {
    if (template) {
      setTitle(template.title);
      setInstructions(template.instructions);
      setOutputsDescription(template.outputsDescription ?? '');
    }
  }, [template?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSubmit(inputs: Record<string, unknown>) {
    if (!templateId || !assignee || !template) {
      setSubmitError(t('Pick a team member before running this job.'));
      return;
    }
    setSubmitError(null);
    const body: CreateJobBody = {
      job_template_id: templateId,
      ...(assignee.kind === 'member'
        ? { assigned_member_id: assignee.id }
        : { staff_profile_id: assignee.id }),
      inputs,
    };
    const trimmedTitle = title.trim();
    if (trimmedTitle && trimmedTitle !== template.title) {
      body.title = trimmedTitle;
    }
    if (instructions.trim() && instructions !== template.instructions) {
      body.instructions = instructions;
    }
    const trimmedOutputsDescription = outputsDescription.trim();
    if (trimmedOutputsDescription !== (template.outputsDescription ?? '').trim()) {
      body.outputs_description = trimmedOutputsDescription || null;
    }
    if (rememberOutputs) body.remember_outputs = true;
    // Top-level attachments only flow on free-form templates — schema-driven
    // templates expect attachments to come through their schema's
    // x-linqage-attachment fields. Mirrors RunJobSheet's rule.
    const templateIsFreeForm = !template.inputSchema;
    if (templateIsFreeForm && attachmentIds.length > 0) {
      body.attachment_ids = attachmentIds;
    }
    try {
      const job = await create.mutateAsync(body);
      void navigate(`/jobs/${job.id}`);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : t('Failed to create job.'));
    }
  }

  // No template means there's nothing to render — the Jobs hub is now the
  // template-picker surface.
  if (!templateId) {
    return <Navigate to="/jobs" replace />;
  }
  if (!template) {
    return <p className={styles.bannerSub}>{t('Loading…')}</p>;
  }

  const inputSchema = (template.inputSchema as JsonSchema | null) ?? null;
  const schemaHasFields = hasSchemaFields(inputSchema);
  const canSubmit = Boolean(assignee) && !create.isPending && !hasPendingUploads;
  const titleIsBlank = !title.trim();
  const willAdd = assignee?.kind === 'staff_profile';
  const startLabel = willAdd ? t('Add & Start') : t('Run');

  return (
    <article className={styles.bannerCard}>
      <header className={styles.banner}>
        <div className={styles.bannerLeft}>
          <span className={styles.iconSquare} aria-hidden>
            {initials(template.title)}
          </span>
          <div className={styles.bannerTitleWrap}>
            <label className={styles.nameField}>
              <span className={styles.nameLabel}>{t('Run')}</span>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                aria-label={t('Job name')}
                aria-invalid={titleIsBlank}
                className={styles.nameInput}
                maxLength={200}
                spellCheck="false"
              />
            </label>
            {template.description ? (
              <p className={styles.bannerSub}>{template.description}</p>
            ) : null}
          </div>
        </div>
        <div className={styles.bannerActions}>
          <Button
            variant="ghost"
            onClick={() => void navigate('/jobs')}
            disabled={create.isPending}
          >
            {t('Cancel')}
          </Button>
          <Button
            variant="primary"
            type="submit"
            form={FORM_ID}
            disabled={!canSubmit || titleIsBlank}
            isLoading={create.isPending}
          >
            {startLabel}
          </Button>
        </div>
      </header>

      <div className={styles.bannerBody}>
        {submitError ? (
          <Alert variant="error" onDismiss={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        ) : null}

        <div className={styles.grid}>
          {/* ── Primary column ────────────────────────────────────── */}
          <div className={styles.primary}>
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>{t('Inputs')}</h3>
              {template.inputsDescription ? (
                <p className={styles.sectionHelp}>{template.inputsDescription}</p>
              ) : null}
              {schemaHasFields && inputSchema ? (
                <SchemaForm
                  schema={inputSchema}
                  formId={FORM_ID}
                  hideSubmit
                  defaultValues={handoff?.prefillInputs ?? undefined}
                  onSubmit={(values) => {
                    void handleSubmit(values);
                  }}
                  onUploadAttachment={async (file) => {
                    const res = await upload.mutateAsync({ file });
                    return { attachment_id: res.attachment.id };
                  }}
                  isPending={create.isPending || hasPendingUploads}
                />
              ) : (
                <form
                  id={FORM_ID}
                  className={styles.freeForm}
                  onSubmit={(e) => {
                    e.preventDefault();
                    const inputs = freeText.trim() ? { text: freeText } : {};
                    void handleSubmit(inputs);
                  }}
                >
                  <Tabs.Root defaultValue="files">
                    <Tabs.List>
                      <Tabs.Trigger value="files">
                        {t('Files')}
                        {attachmentIds.length > 0 ? (
                          <span className={styles.tabCount}>{attachmentIds.length}</span>
                        ) : null}
                      </Tabs.Trigger>
                      <Tabs.Trigger value="text">{t('Text')}</Tabs.Trigger>
                    </Tabs.List>
                    <Tabs.Content value="files">
                      <JobAttachmentTable
                        attachments={pendingAttachments}
                        onChange={setPendingAttachments}
                      />
                    </Tabs.Content>
                    <Tabs.Content value="text">
                      <textarea
                        className={styles.freeTextArea}
                        value={freeText}
                        onChange={(e) => setFreeText(e.target.value)}
                        placeholder={t('Add additional context here')}
                        rows={9}
                        aria-label={t('Additional context')}
                      />
                    </Tabs.Content>
                  </Tabs.Root>
                </form>
              )}
            </section>

            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>{t('Assigned Member')}</h3>
              <RecommendedMembersPanel
                templateId={templateId}
                value={assignee}
                onChange={setAssignee}
              />
              {willAdd && assignee ? (
                <PendingAddBanner staffProfileId={assignee.id} templateId={templateId} />
              ) : null}
            </section>
          </div>

          {/* ── Secondary column ──────────────────────────────────── */}
          <aside className={styles.secondary}>
            <section className={styles.section}>
              <h3 className={styles.secondaryTitle}>{t('Customize for this run')}</h3>
              <label className={styles.fieldLabel}>
                {t('Instructions')}
                <textarea
                  className={styles.instructionsArea}
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  rows={6}
                  aria-label={t('Task instructions')}
                />
              </label>
              <label className={styles.fieldLabel}>
                {t('Expected outputs')}
                <textarea
                  className={styles.instructionsArea}
                  value={outputsDescription}
                  onChange={(e) => setOutputsDescription(e.target.value)}
                  rows={4}
                  placeholder={t('Describe the deliverable shape this run should produce.')}
                  aria-label={t('Expected outputs')}
                />
              </label>
              <p className={styles.fieldHint}>
                {t(
                  'Note: structured output fields defined by the template are fixed for this run.',
                )}
              </p>
            </section>

            <div className={styles.rememberRow}>
              <input
                id="job-remember-outputs"
                type="checkbox"
                checked={rememberOutputs}
                onChange={(e) => setRememberOutputs(e.target.checked)}
              />
              <label htmlFor="job-remember-outputs" className={styles.rememberText}>
                <strong>{t("Remember this job's outputs")}</strong>
                <small>
                  {t(
                    'When checked, the assigned Member will be able to recall this job in future chats.',
                  )}
                </small>
              </label>
            </div>
          </aside>
        </div>
      </div>
    </article>
  );
}
